import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { MECETactic, MECEPillar, ImplementationStatus } from '../../../models'
import { getTacticNumber } from '../../../utils/meceNumbering'
import StatusDropdown from '../../shared/StatusDropdown'
import './TacticCard.css'

interface TacticCardProps {
  tactic: MECETactic
  tacticId: string  // Unique ID for the tactic
  pillarName: string
  pillar?: MECEPillar          // Full pillar object for numbering utility
  pillarIndex?: number         // Pillar index for numbering
  tacticIndex?: number         // Tactic index for numbering
  isReorderMode: boolean
  userPriority?: number
  implementationStatus?: ImplementationStatus
  onStatusChange?: (status: ImplementationStatus) => void
  onQuickAsk?: () => void
  onViewEvidence?: () => void
  onEdit?: () => void
}

export const TacticCard: React.FC<TacticCardProps> = ({
  tactic,
  tacticId,
  pillarName: _pillarName,  // Kept for API compatibility
  pillar,
  pillarIndex,
  tacticIndex,
  isReorderMode,
  userPriority,
  implementationStatus = 'not_started',
  onStatusChange,
  onQuickAsk,
  onViewEvidence,
  onEdit
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: tacticId,
    disabled: !isReorderMode
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1
  }

  const evidenceCount = tactic.node_ids?.length || 0

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`tactic-card-draggable ${isDragging ? 'dragging' : ''} ${isReorderMode ? 'reorder-mode' : ''}`}
      {...attributes}
    >
      {/* Drag Handle - only visible in reorder mode */}
      {isReorderMode && (
        <div className="drag-handle" {...listeners}>
          <span className="handle-icon"></span>
        </div>
      )}

      {/* Priority Badge - shows position in reorder mode */}
      {userPriority !== undefined && isReorderMode && (
        <div className="priority-badge">
          #{userPriority}
        </div>
      )}

      {/* Tactic Content */}
      <div className="tactic-content">
        <h4 className="tactic-title">
          {pillar && pillarIndex !== undefined && tacticIndex !== undefined && (
            <span className="tactic-number">{getTacticNumber(pillar, pillarIndex, tactic, tacticIndex)}</span>
          )}
          {tactic.title}
        </h4>
        <p className="tactic-description">{tactic.description}</p>

        {/* Rationale if available */}
        {tactic.rationale && (
          <p className="tactic-rationale">
            <strong>Rationale:</strong> {tactic.rationale}
          </p>
        )}

        {/* Actions - hidden during reorder mode */}
        {!isReorderMode && (
          <div className="tactic-actions">
            {/* Status Dropdown */}
            {onStatusChange && (
              <StatusDropdown
                value={implementationStatus}
                onChange={onStatusChange}
                size="small"
              />
            )}
            {evidenceCount > 0 && onViewEvidence && (
              <button
                onClick={onViewEvidence}
                className="btn-evidence"
                title="View supporting evidence"
              >
                Evidence ({evidenceCount})
              </button>
            )}
            {onQuickAsk && (
              <button
                onClick={onQuickAsk}
                className="btn-quick-ask"
                title="Ask a question about this tactic"
              >
                Quick Ask
              </button>
            )}
            {onEdit && (
              <button
                onClick={onEdit}
                className="btn-edit-tactic"
                title="Edit this tactic"
              >
                Edit
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
